import React, { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { LuAlertCircle, LuFileText, LuPhone, LuUser } from "react-icons/lu";

import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  getEditAccountType,
  getIsAccountTypeModalOpen,
  toggleAccountTypeModal,
} from "../../features/accounts/accountTypesSlice";
import { IFormInput } from "../../types/accountTypes";
import Button from "../ui/Button";
import Input from "../ui/Input";
import { Dialog, DialogContent, DialogFooter, DialogHeader } from "../ui/Dialog";
import cx from "../ui/cx";
import {
  buttonSpinnerClass,
  dialogMobileBodyClass,
  dialogMobileContentClass,
  dialogMobileFooterClass,
  dialogMobileFormClass,
  dialogMobileHeaderClass,
  dialogTitleClass,
  fieldErrorIconClass,
  fieldErrorTextClass,
  fieldInputClass,
  fieldLabelClass,
  formFieldIconClass,
} from "../ui/styles";

type Props = {
  handleSaveAccountType: (data: IFormInput) => void;
};

const AccountTypeFormModal = ({ handleSaveAccountType }: Props) => {
  const dispatch = useAppDispatch();
  const editAccountType = useAppSelector(getEditAccountType);
  const isAccountTypeModalOpen = useAppSelector(getIsAccountTypeModalOpen);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const {
    handleSubmit,
    register,
    reset,
    formState: { errors },
  } = useForm<IFormInput>({
    defaultValues: { name: "", phone: "", description: "" },
  });

  const handleOnSubmit: SubmitHandler<IFormInput> = async (data) => {
    setIsLoading(true);
    await handleSaveAccountType(data);
    handleCloseFormModal();
    setIsLoading(false);
  };

  const handleCloseFormModal = () => {
    reset();
    dispatch(toggleAccountTypeModal(false));
  };

  useEffect(() => {
    if (editAccountType && editAccountType.id) {
      reset({
        name: editAccountType.name,
        phone: editAccountType.phone,
        description: editAccountType.description,
      });
    } else {
      reset({ name: "", phone: "", description: "" });
    }
  }, [editAccountType, isAccountTypeModalOpen, reset]);

  return (
    <Dialog
      open={isAccountTypeModalOpen === true}
      onClose={handleCloseFormModal}
    >
      <DialogContent
        size="md"
        className={cx(
          "font-inter max-h-[90vh] overflow-y-auto",
          dialogMobileContentClass
        )}
      >
        <DialogHeader className={dialogMobileHeaderClass}>
          <h2 className={dialogTitleClass}>પાર્ટી ની માહિતી</h2>
        </DialogHeader>

        <form
          onSubmit={handleSubmit(handleOnSubmit)}
          className={cx("space-y-4", dialogMobileFormClass)}
        >
          <div className={cx("space-y-4", dialogMobileBodyClass)}>
            <div className="space-y-2">
              <label className={fieldLabelClass}>
                પાર્ટીનું નામ <span className="text-red-500">*</span>
              </label>
              <div className={cx("relative", errors.name && "error")}>
                <LuUser className={formFieldIconClass} />
                <Input
                  type="text"
                  placeholder="પાર્ટીનું નામ દાખલ કરો"
                  className={fieldInputClass(!!errors.name)}
                  {...register("name", { required: true })}
                />
                {errors.name && (
                  <LuAlertCircle className={fieldErrorIconClass} />
                )}
              </div>
              {errors.name && (
                <p className={fieldErrorTextClass}>
                  <LuAlertCircle className="h-4 w-4 mr-1" />
                  પાર્ટીનું નામ દાખલ કરો
                </p>
              )}
            </div>

            <div className="space-y-2">
              <label className={fieldLabelClass}>મોબાઇલ નંબર</label>
              <div className="relative">
                <LuPhone className={formFieldIconClass} />
                <Input
                  type="text"
                  placeholder="મોબાઇલ નંબર દાખલ કરો"
                  className={fieldInputClass()}
                  {...register("phone", { required: false })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className={fieldLabelClass}>નોંધ</label>
              <div className="relative">
                <LuFileText className={formFieldIconClass} />
                <Input
                  type="text"
                  placeholder="નોંધ દાખલ કરો"
                  className={fieldInputClass()}
                  {...register("description", { required: false })}
                />
              </div>
            </div>
          </div>

          <DialogFooter className={dialogMobileFooterClass}>
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={handleCloseFormModal}
              disabled={isLoading}
            >
              રદ કરો
            </Button>

            <Button
              type="submit"
              variant="primary"
              className="flex-1"
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="flex items-center">
                  <span className={buttonSpinnerClass}></span>
                  સાચવી રહ્યા છીએ...
                </span>
              ) : (
                "સાચવો"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AccountTypeFormModal;
